import {
  createPersistedCode,
  PENDING_INVITE_CODE_TTL_MS,
} from '../../utils/persisted-code';
import { safeLocalStorage } from '@teachino/shared-util';
import { TenantInviteAcceptanceIntent } from '~/graphql';
import { storeTokens } from '~/plugins/urql';
import {
  ANONYMOUS_LANGUAGE_STORAGE_KEY,
  type SystemLanguage,
} from '@teachino/shared-i18n';

const TENANT_INVITE_INTENT_STORAGE_KEY = 'tenant_invite_acceptance_intent';

// Persisted rather than kept in memory: the signup it waits for can finish in a
// fresh tab (the link in the verification e-mail) or after a provider round
// trip.
const codeToAcceptAfterLogin = createPersistedCode(
  'tenant_invite_code_to_accept',
  PENDING_INVITE_CODE_TTL_MS,
);

export default function () {
  const acceptTenantInvite = useAcceptTenantInviteMutation();
  const refreshTokenMutation = useRefreshTokenMutation();

  function setCodeToAcceptAfterLogin(
    newCode: string,
    intent: Exclude<
      TenantInviteAcceptanceIntent,
      TenantInviteAcceptanceIntent.Direct
    >,
    language: SystemLanguage,
  ) {
    codeToAcceptAfterLogin.write(newCode);
    safeLocalStorage.setItem(TENANT_INVITE_INTENT_STORAGE_KEY, intent);
    safeLocalStorage.setItem(
      ANONYMOUS_LANGUAGE_STORAGE_KEY,
      JSON.stringify({
        language,
        expiresAt: Date.now() + PENDING_INVITE_CODE_TTL_MS,
      }),
    );
  }

  async function acceptCodeAfterLogin(): Promise<boolean> {
    const code = codeToAcceptAfterLogin.read();
    if (!code) {
      safeLocalStorage.removeItem(TENANT_INVITE_INTENT_STORAGE_KEY);
      return true;
    }
    // Taken before the attempt, so the invite is accepted once instead of on
    // every later load of the user.
    codeToAcceptAfterLogin.clear();
    const storedIntent = safeLocalStorage.getItem(
      TENANT_INVITE_INTENT_STORAGE_KEY,
    );
    safeLocalStorage.removeItem(TENANT_INVITE_INTENT_STORAGE_KEY);
    const intent: TenantInviteAcceptanceIntent =
      storedIntent === TenantInviteAcceptanceIntent.Signup ||
      storedIntent === TenantInviteAcceptanceIntent.Login
        ? storedIntent
        : TenantInviteAcceptanceIntent.Direct;
    const accepted = await acceptCode(code, intent);
    if (accepted && intent === TenantInviteAcceptanceIntent.Signup) {
      await reissueTokens();
    }
    return accepted;
  }

  // A signup-intent acceptance may have changed the user's language, which
  // the access token carries (`lng`) for backend translations. Re-issue the
  // tokens so the session does not keep the pre-invite language until the
  // next refresh.
  async function reissueTokens(): Promise<void> {
    const currentRefreshToken = safeLocalStorage.getItem('refreshToken');
    if (!currentRefreshToken) {
      return;
    }
    const { data } = await refreshTokenMutation.executeMutation({
      refreshTokenInput: { refreshToken: currentRefreshToken },
    });
    if (data?.refreshToken) {
      storeTokens(data.refreshToken);
    }
  }

  async function acceptCode(
    code: string,
    intent: TenantInviteAcceptanceIntent =
      TenantInviteAcceptanceIntent.Direct,
  ): Promise<boolean> {
    const { error } = await acceptTenantInvite.executeMutation({
      acceptTenantInviteInput: {
        code,
        intent,
      },
    });
    return !error;
  }

  return {
    setCodeToAcceptAfterLogin,
    acceptCodeAfterLogin,
    acceptCode,
  };
}
